import { IconX } from "@tabler/icons-react";
import { useRolesDisponibles } from "@/features/admin-usuarios/hooks/use-roles-disponibles";
import { NumberInput, Select, Stack, Switch, Text } from "@/components/ui";

/**
 * Gestión Documental (fase 2) · configuración documental de una categoría o de un tipo de
 * documento (spec §6): revisión obligatoria, roles revisores, vigencia y comportamiento del
 * documento vencido en los tres momentos (D-13). En un TIPO cada valor es opcional (null = hereda
 * de su categoría · D-10).
 */
export interface ConfiguracionDocumental {
  requiereRevision: boolean | null;
  rolesRevisores: string[] | null;
  manejaVigencia: boolean | null;
  vigenciaCantidad: number | null;
  vigenciaUnidad: UnidadVigencia | null;
  alVencerEnvio: ComportamientoAlVencer | null;
  alVencerRevision: ComportamientoAlVencer | null;
  alVencerAutorizacion: ComportamientoAlVencer | null;
}

export type UnidadVigencia = "Dias" | "Meses" | "Anios";
export type ComportamientoAlVencer = "SoloMarca" | "Bloquea" | "DecideRevisor";

export const CONFIG_POR_DEFECTO: ConfiguracionDocumental = {
  requiereRevision: false,
  rolesRevisores: [],
  manejaVigencia: false,
  vigenciaCantidad: null,
  vigenciaUnidad: null,
  alVencerEnvio: "SoloMarca",
  alVencerRevision: "SoloMarca",
  alVencerAutorizacion: "SoloMarca",
};

export const CONFIG_HEREDA: ConfiguracionDocumental = {
  requiereRevision: null,
  rolesRevisores: null,
  manejaVigencia: null,
  vigenciaCantidad: null,
  vigenciaUnidad: null,
  alVencerEnvio: null,
  alVencerRevision: null,
  alVencerAutorizacion: null,
};

export const UNIDADES: { value: UnidadVigencia; label: string }[] = [
  { value: "Dias", label: "Días" },
  { value: "Meses", label: "Meses" },
  { value: "Anios", label: "Años" },
];

export const COMPORTAMIENTOS: { value: ComportamientoAlVencer; label: string }[] = [
  { value: "SoloMarca", label: "Solo se marca como vencido" },
  { value: "Bloquea", label: "Bloquea hasta reemplazarlo" },
  { value: "DecideRevisor", label: "Lo decide el revisor" },
];

export function textoVigencia(c: Pick<ConfiguracionDocumental, "manejaVigencia" | "vigenciaCantidad" | "vigenciaUnidad">): string {
  if (!c.manejaVigencia) return "Sin vigencia";
  const unidad = UNIDADES.find((u) => u.value === c.vigenciaUnidad)?.label.toLowerCase() ?? "";
  return `${c.vigenciaCantidad ?? "?"} ${unidad}`;
}

const HEREDA = "__hereda__";

/**
 * Campos de la configuración documental. `heredable` = es de un tipo: cada campo ofrece
 * «Heredar de la categoría» (valor null).
 */
export function ConfiguracionDocumentalFields({
  value,
  onChange,
  heredable = false,
  disabled = false,
}: {
  value: ConfiguracionDocumental;
  onChange: (v: ConfiguracionDocumental) => void;
  heredable?: boolean;
  disabled?: boolean;
}) {
  const roles = useRolesDisponibles();
  const set = (patch: Partial<ConfiguracionDocumental>) => onChange({ ...value, ...patch });

  const opcionesSiNo = [
    ...(heredable ? [{ value: HEREDA, label: "Heredar de la categoría" }] : []),
    { value: "true", label: "Sí" },
    { value: "false", label: "No" },
  ];
  const aSiNo = (b: boolean | null) => (b === null ? HEREDA : String(b));
  const deSiNo = (v: string) => (v === HEREDA ? null : v === "true");

  const comportamientos = [...(heredable ? [{ value: HEREDA, label: "Heredar de la categoría" }] : []), ...COMPORTAMIENTOS];
  const rolesSeleccionados = value.rolesRevisores ?? [];
  const rolesLibres = roles.filter((r) => !rolesSeleccionados.includes(r));

  return (
    <Stack gap="md">
      <Text size="sm" fw={600}>
        Revisión documental
      </Text>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {heredable ? (
          <Select
            label="¿Requiere revisión?"
            options={opcionesSiNo}
            value={aSiNo(value.requiereRevision)}
            onValueChange={(v) => set({ requiereRevision: deSiNo(v) })}
            disabled={disabled}
          />
        ) : (
          <Switch
            label="Requiere revisión"
            description="Por defecto la revisión no es obligatoria · las reglas por área o solicitante pueden cambiarlo."
            checked={value.requiereRevision ?? false}
            onCheckedChange={(c) => set({ requiereRevision: c })}
            disabled={disabled}
          />
        )}
        <div className="flex flex-col gap-1">
          <Select
            label="Roles revisores"
            description={heredable ? "Vacío = los de la categoría." : "Quién revisa los documentos de esta categoría."}
            placeholder={rolesLibres.length ? "Agregar rol…" : "Sin más roles"}
            options={rolesLibres.map((r) => ({ value: r, label: r }))}
            value=""
            onValueChange={(r) => r && set({ rolesRevisores: [...rolesSeleccionados, r] })}
            disabled={disabled || rolesLibres.length === 0}
            searchable
          />
          {rolesSeleccionados.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {rolesSeleccionados.map((r) => (
                <span key={r} className="inline-flex items-center gap-1 rounded-sm bg-muted px-1.5 py-0.5 text-xs">
                  {r}
                  {!disabled && (
                    <button
                      type="button"
                      aria-label={`Quitar ${r}`}
                      onClick={() => set({ rolesRevisores: rolesSeleccionados.filter((x) => x !== r) })}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <IconX size={10} />
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <Text size="sm" fw={600}>
        Vigencia
      </Text>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {heredable ? (
          <Select
            label="¿Maneja vigencia?"
            options={opcionesSiNo}
            value={aSiNo(value.manejaVigencia)}
            onValueChange={(v) => set({ manejaVigencia: deSiNo(v) })}
            disabled={disabled}
          />
        ) : (
          <Switch
            label="Maneja vigencia"
            description="Se cuenta desde la fecha de emisión que captura el solicitante."
            checked={value.manejaVigencia ?? false}
            onCheckedChange={(c) => set({ manejaVigencia: c })}
            disabled={disabled}
          />
        )}
        {value.manejaVigencia && (
          <>
            <NumberInput
              label="Periodo"
              value={value.vigenciaCantidad ?? undefined}
              onChange={(v) => set({ vigenciaCantidad: typeof v === "number" ? v : null })}
              min={1}
              max={3650}
              step={1}
              required
              disabled={disabled}
            />
            <Select
              label="Unidad"
              options={UNIDADES}
              value={value.vigenciaUnidad ?? undefined}
              onValueChange={(v) => set({ vigenciaUnidad: v as UnidadVigencia })}
              placeholder="Días, meses o años"
              required
              disabled={disabled}
            />
          </>
        )}
      </div>

      <Text size="sm" fw={600}>
        Si el documento está vencido…
      </Text>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {(
          [
            ["alVencerEnvio", "Al enviar a autorización"],
            ["alVencerRevision", "En la revisión"],
            ["alVencerAutorizacion", "En la autorización"],
          ] as const
        ).map(([campo, label]) => (
          <Select
            key={campo}
            label={label}
            options={comportamientos}
            value={value[campo] ?? HEREDA}
            onValueChange={(v) => set({ [campo]: v === HEREDA ? null : (v as ComportamientoAlVencer) })}
            disabled={disabled}
          />
        ))}
      </div>
    </Stack>
  );
}

/** Validación de la vigencia en el cliente (el servidor valida lo mismo). */
export function configuracionValida(c: ConfiguracionDocumental, completa: boolean): string | null {
  if (c.manejaVigencia && (!c.vigenciaCantidad || c.vigenciaCantidad < 1 || !c.vigenciaUnidad)) return "Indica el periodo y la unidad de vigencia.";
  if (completa && c.requiereRevision && (c.rolesRevisores ?? []).length === 0) return "Si la revisión es obligatoria indica al menos un rol revisor.";
  return null;
}
